/**
 * Area route guard — PURE decision mapping (PR-ONB-RESUME-1B, lock #5).
 *
 * The AreaLayout collects (firebase session?, GET /area/me outcome) and asks
 * this function what to render. Keeping the mapping pure makes the guard
 * matrix unit-testable without React or Firebase:
 *
 *   no session                  → LOGIN
 *   me ok                       → ALLOW
 *   401 (expired token)         → RETRY_AUTH (refresh token once, then LOGIN)
 *   ACCOUNT_CONVERTED           → CONVERTED (future redirect to the portal)
 *   ACCOUNT_DISABLED            → DISABLED (blocked screen)
 *   ACCOUNT_NOT_FOUND           → BIND (login ok but never bound — offer register)
 *   503 RESUMABLE_DISABLED      → MAINTENANCE (back to legacy wizard)
 *   network / unknown           → ERROR (retry CTA)
 */

import type { AreaFailureKind, AreaMe } from '@/lib/areaApi';

/**
 * Bind identity comparison (review fix #1). `markBound` must be IDEMPOTENT:
 * re-publishing an identical bind would change the context value identity,
 * re-create the guard's evaluate callback and re-fire GET /area/me — an
 * infinite request loop. Pure so the no-loop invariant is unit-testable.
 */
export interface BindInfoLike {
  requestPublicId: string;
  draftRevision: number;
}

export function sameBind(
  previous: BindInfoLike | null,
  next: BindInfoLike,
): boolean {
  return (
    previous !== null &&
    previous.requestPublicId === next.requestPublicId &&
    previous.draftRevision === next.draftRevision
  );
}

export type AreaGuardDecision =
  | { action: 'LOGIN' }
  | { action: 'ALLOW'; me: AreaMe }
  | { action: 'RETRY_AUTH' }
  | { action: 'BIND' }
  | { action: 'CONVERTED' }
  | { action: 'DISABLED' }
  | { action: 'MAINTENANCE' }
  | { action: 'ERROR' };

export function resolveAreaGuard(input: {
  hasSession: boolean;
  me?: { ok: true; data: AreaMe } | { ok: false; kind: AreaFailureKind };
}): AreaGuardDecision {
  if (!input.hasSession) return { action: 'LOGIN' };
  const me = input.me;
  if (!me) return { action: 'ERROR' };
  if (me.ok) return { action: 'ALLOW', me: me.data };
  switch (me.kind) {
    case 'AUTH_INVALID':
      return { action: 'RETRY_AUTH' };
    case 'ACCOUNT_NOT_FOUND':
      return { action: 'BIND' };
    case 'ACCOUNT_CONVERTED':
      return { action: 'CONVERTED' };
    case 'ACCOUNT_DISABLED':
      return { action: 'DISABLED' };
    case 'RESUMABLE_DISABLED':
      return { action: 'MAINTENANCE' };
    default:
      return { action: 'ERROR' };
  }
}
